import { AxiosError } from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  Cart,
  CartItem,
  CartItemCustomization,
  CartItemCustomizationOption,
} from '../../../app/data/models/cart';
import api from '../../../app/api/api';
import { replaceObjectById } from '../../../app/utils/array';
import { Order } from '../../../app/data/models/orders/order';
import CurrentPageItem from '@app/data/models/common/currentPageItem';
import { calculateCartTotals } from '../../customer/cart/cartHelper';
import Restaurant from '@app/data/models/restaurant';

const emptyCart = ({
  cartId,
  restaurantId,
  waiterId,
  waiterName,
}: {
  cartId: string;
  restaurantId: string;
  waiterId?: string;
  waiterName?: string;
}): Cart => ({
  id: cartId,
  restaurantName: undefined,
  restaurantId,
  orderItems: [],
  subtotal: 0,
  totalTax: 0,
  totalPrice: 0,
  totalQuantity: 0,
  defaultTaxRate: 0,
  waiterId,
  waiterName,
});

const loadCartsFromStorage = (restaurantId: string | undefined): Cart[] => {
  if (!restaurantId) return [];

  try {
    const savedCarts = localStorage.getItem(`carts_${restaurantId}`);
    if (savedCarts) {
      return JSON.parse(savedCarts);
    }
  } catch (error) {
    console.error('Error loading carts from storage:', error);
  }
  return [];
};

interface WaiterOrderState {
  carts: Cart[];
  orders: Order[];
  status: string;
  orderTab: number;
  waiterOrdersStatus: string;
  waiterOrderNextPageId?: string | null;
  waiterOrderPreviousPageId?: string | null;
  restaurant: Restaurant | null;
}

const initialState: WaiterOrderState = {
  carts: [],
  orders: [],
  status: 'idle',
  orderTab: 0,
  waiterOrdersStatus: 'idle',
  restaurant: null,
};

export const loadRestaurantCartsAsync = createAsyncThunk<
  void,
  string,
  { rejectValue: { error: unknown } }
>('waiter/loadRestaurantCarts', async (restaurantId, thunkApi) => {
  try {
    const carts = loadCartsFromStorage(restaurantId);
    thunkApi.dispatch(setCarts(carts));
    await thunkApi.dispatch(loadRestaurantDetailsAsync(restaurantId));
  } catch (error: unknown) {
    return thunkApi.rejectWithValue({ error });
  }
});

export const loadRestaurantDetailsAsync = createAsyncThunk<
  Restaurant,
  string,
  { rejectValue: { error: unknown } }
>('waiter/loadRestaurantDetails', async (restaurantId, thunkApi) => {
  try {
    const restaurantDetails =
      await api.CustomerRestaurant.details(restaurantId);
    return restaurantDetails;
  } catch (error: unknown) {
    return thunkApi.rejectWithValue({ error });
  }
});

export const getWaiterOrdersAsync = createAsyncThunk<
  CurrentPageItem<Order>,
  { restaurantId: string; waiterId: string; nextPageId?: string }
>(
  'waiter/orders/list',
  async ({ restaurantId, waiterId, nextPageId }, thunkApi) => {
    try {
      return await api.RestaurantWaiter.orders(
        restaurantId,
        waiterId,
        nextPageId
      );
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const createWaiterOrderAsync = createAsyncThunk<Order, { cart: Cart }>(
  'orders/create',
  async ({ cart }, thunkApi) => {
    try {
      const restaurantId = cart.restaurantId;
      if (!restaurantId) return null;
      const order = await api.Cart.create(restaurantId, cart);
      thunkApi.dispatch(clearWaiterCart({ restaurantId, cartId: cart.id }));
      return order;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        return thunkApi.rejectWithValue({ error: error.response?.data });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const updateCustomerOrder = (order: Order) => {
  const ordersJSON = localStorage.getItem('orders');
  const orders = JSON.parse(ordersJSON ?? '[]');
  const orderIndex = orders.findIndex((o: Order) => o.id === order.id);
  if (orderIndex !== -1) {
    orders[orderIndex] = order;
  } else {
    orders.push(order);
  }
  localStorage.setItem('orders', JSON.stringify(orders));
};

const calculateItemTotal = (cartItem: CartItem): number => {
  let total = cartItem.price;
  if (cartItem.customizations) {
    cartItem.customizations.forEach((customization) => {
      customization.options.forEach((option) => {
        total += option.price * option.quantity;
      });
    });
  }
  return total * cartItem.quantity;
};

export const waiterOrderSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setOrderTab: (state, action) => {
      state.orderTab = action.payload;
    },
    setCarts: (state, action) => {
      state.carts = action.payload;
    },
    updateProductQuantity: (state, action) => {
      const { cartItem, quantity, restaurantId, cartId, waiterId, waiterName } =
        action.payload;
      if (!cartId) {
        console.warn('No cartId provided in updateProductQuantity');
        return;
      }
      const cart =
        state.carts.find((cart: Cart) => cart.id === cartId) ??
        emptyCart({ cartId, restaurantId, waiterId, waiterName });
      const itemIndex = cart.orderItems.findIndex(
        (item: CartItem) => item.cartItemId === cartItem.cartItemId
      );

      if (itemIndex !== -1) {
        const existingItem = cart.orderItems[itemIndex];
        existingItem.quantity += quantity;
        if (existingItem.quantity <= 0) {
          cart.orderItems.splice(itemIndex, 1);
        } else {
          existingItem.totalPrice = calculateItemTotal(existingItem);
        }
      } else if (quantity > 0) {
        const newItem = { ...cartItem, quantity };
        newItem.totalPrice = calculateItemTotal(newItem);
        cart.orderItems.push(newItem);
      }

      const updatedCart = calculateCartTotals(
        cart,
        state.restaurant?.settings?.priceIncludesTax ?? false
      );

      const cartIndex = state.carts.findIndex((c) => c.id === cartId);
      if (cartIndex !== -1) {
        state.carts[cartIndex] = updatedCart;
      } else {
        state.carts.push(updatedCart);
      }

      // Save to localStorage
      localStorage.setItem(
        `carts_${restaurantId}`,
        JSON.stringify(state.carts)
      );
    },
    clearWaiterCart: (state, action) => {
      const { cartId, restaurantId } = action.payload;
      const cartIndex = state.carts.findIndex((cart) => cart.id === cartId);
      if (cartIndex !== -1) {
        state.carts = state.carts.filter((cart) => cart.id !== cartId);
        localStorage.setItem(
          `carts_${restaurantId}`,
          JSON.stringify(state.carts)
        );
      }
    },
    updateCartDetails: (state, action) => {
      const { cartId, restaurantId, updates } = action.payload;
      const cartIndex = state.carts.findIndex((cart) => cart.id === cartId);

      if (cartIndex !== -1) {
        state.carts[cartIndex] = {
          ...state.carts[cartIndex],
          ...updates,
        };
      } else {
        state.carts.push({
          ...emptyCart({
            cartId,
            restaurantId,
            waiterId: updates.waiterId,
            waiterName: updates.waiterName,
          }),
          ...updates,
        });
      }

      // Save to localStorage
      localStorage.setItem(
        `carts_${restaurantId}`,
        JSON.stringify(state.carts)
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createWaiterOrderAsync.pending, (state) => {
      state.status = 'pendingCreateRestaurant';
    });
    builder.addCase(createWaiterOrderAsync.fulfilled, (state, action) => {
      const order = action.payload;
      state.orders = replaceObjectById(state.orders, order.id, order);
      state.status = 'idle';
    });
    builder.addCase(createWaiterOrderAsync.rejected, (state) => {
      state.status = 'idle';
    });
    builder.addCase(getWaiterOrdersAsync.fulfilled, (state, action) => {
      state.orders = action.payload.data;
      state.waiterOrderNextPageId = action.payload.nextPageId;
      state.waiterOrderPreviousPageId = action.payload.previousPageId;
      state.waiterOrdersStatus = 'idle';
    });
    builder.addCase(getWaiterOrdersAsync.rejected, (state) => {
      state.waiterOrdersStatus = 'idle';
    });
    builder.addCase(getWaiterOrdersAsync.pending, (state) => {
      state.waiterOrdersStatus = 'pendingGetWaiterOrders';
    });
    builder.addCase(loadRestaurantCartsAsync.pending, (state) => {
      state.status = 'pendingLoadRestaurantCarts';
    });
    builder.addCase(loadRestaurantCartsAsync.rejected, (state) => {
      state.status = 'idle';
    });
    builder.addCase(loadRestaurantDetailsAsync.pending, (state) => {
      state.status = 'pendingLoadRestaurantDetails';
    });
    builder.addCase(loadRestaurantDetailsAsync.fulfilled, (state, action) => {
      const restaurant = action.payload;
      state.restaurant = restaurant;
      state.carts = state.carts.map((cart) =>
        calculateCartTotals(
          {
            ...cart,
            defaultTaxRate: restaurant.defaultTaxRate,
            restaurantName: restaurant.name,
          },
          restaurant.settings?.priceIncludesTax ?? false
        )
      );
      state.status = 'idle';
    });
    builder.addCase(loadRestaurantDetailsAsync.rejected, (state) => {
      state.status = 'idle';
    });
  },
});

export const {
  setOrderTab,
  setCarts,
  updateProductQuantity,
  clearWaiterCart,
  updateCartDetails,
} = waiterOrderSlice.actions;
